"use client";

import { motion, useReducedMotion } from "framer-motion";
import { Check, PhoneCall, ArrowRight, ShieldCheck, Zap, ThermometerSnowflake, Layers, Lock, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
const features = [{
  icon: ThermometerSnowflake,
  title: "Aluminum Chassis",
  description: "Thick tubular aluminum main rails and aluminum angle cross members provide the trailer's primary chassis structure."
}, {
  icon: Layers,
  title: "Pallet-Duty Floor",
  description: "Each trailer includes an aluminum pallet-duty floor."
}, {
  icon: Lock,
  title: "Interior Materials",
  description: "Interior walls, ceilings, and floors use verified NSF- and USDA-approved materials."
}, {
  icon: ShieldCheck,
  title: "Doorframe and Seals",
  description: "A 304 stainless-steel doorframe and EPDM door seals help retain conditioned air inside the trailer."
}, {
  icon: Sparkles,
  title: "Tongue Jack",
  description: "A tongue jack with foot is positioned at the trailer hitch for raising and lowering during placement."
}, {
  icon: Zap,
  title: "Standard Electrical Connection",
  description: "Each trailer operates using one standard 115-volt, 15-amp electrical circuit. A dedicated circuit is recommended. An optional Honda 3000 generator is available when suitable site power is unavailable."
}];
export default function Features() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="key-features" data-nav="light" className="bg-background py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 border border-accent/40 text-accent-foreground text-xs font-semibold tracking-wider uppercase mb-4 max-w-full">
            <span className="size-2 rounded-full bg-accent inline-block shrink-0" />
            Trailer Specifications
          </div>
          <h2 className="font-default text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4 break-words">
            Standard equipment features
          </h2>
          <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed">
            The following verified equipment features apply to the 6×8, 7×12, and 7×16 towable trailers. Construction, power, loading, and equipment details for the 53-foot reefer are confirmed separately.
          </p>
        </div>

        {/* 2-Column Features Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {features.map((item, index) => {
          return <motion.div key={item.title} variants={itemVariants} data-index={index}>
                <Card className="h-full border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                  <CardHeader className="flex flex-row items-start gap-3 sm:gap-4 space-y-0 p-4 sm:p-6 pb-2 sm:pb-3">
                    <div className="size-10 rounded-md bg-accent text-accent-foreground flex items-center justify-center shrink-0">
                      <Check className="size-5 stroke-[2.5]" />
                    </div>
                    <div className="space-y-1 pt-1 min-w-0">
                      <CardTitle className="font-default text-base sm:text-lg md:text-xl font-semibold text-card-foreground">
                        {item.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0 pl-4 sm:pl-16">
                    <p className="font-default text-sm md:text-base text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Direct Action Strip */}
        <div className="mt-12 md:mt-16 p-5 sm:p-8 rounded-xl border border-border bg-muted/40 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left w-full md:w-auto">
            <h3 className="font-default text-lg md:text-xl font-semibold text-foreground">
              Need help selecting a trailer or arranging delivery?
            </h3>
            <p className="font-default text-sm text-muted-foreground">
              Tell us your storage needs, rental dates, delivery location, available power, and temperature requirements. Trailer suitability and availability are confirmed with each quote.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto font-semibold shadow-xs">
              <Link to="/ContactRequestAQuotePage">
                Request a Quote
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <span className="inline-flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground px-3 py-2 w-full sm:w-auto">
              <PhoneCall className="size-4 shrink-0" />
              <a href="tel:2135218821" className="hover:text-foreground transition-colors">213-521-8821</a>
            </span>
          </div>
        </div>
      </div>
    </section>;
}